import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Phone, Settings2, Video } from "lucide-react";
import api from "@/lib/api";
import { useChat } from "@/context/ChatContext";
import { Avatar } from "@/components/chat/Avatar";
import { MessageBubble } from "@/components/chat/MessageBubble";
import { Composer } from "@/components/chat/Composer";
import { SeenBy } from "@/components/chat/SeenBy";
import { PinnedBanner } from "@/components/chat/PinnedBanner";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { hasRole } from "@/lib/roles";

function seenLabel(conv, reads, msg, meId) {
    const others = conv.members.filter((m) => m.id !== meId);
    const n = others.filter((m) => (reads[m.id] || "") >= msg.created_at).length;
    if (!n) return "";
    return conv.type === "direct" ? "Seen" : n === others.length ? "Seen by all" : `Seen by ${n}`;
}

function ThreadHeader({ conv, online, onBack, onEditGroup }) {
    const { startCall } = useChat();
    const other = conv.other;
    const isOn = other && online.has(other.id);
    const status = conv.type === "announcement" ? "Company-wide · everyone reads, Admin and HR post"
        : conv.type === "group" ? `${conv.members.length} members${conv.auto_sync ? " · synced with department" : ""}`
        : isOn ? "Online" : "Offline — calls work when they're online";
    const call = (kind) => startCall({ id: other.id, name: other.name }, kind);
    return (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-ink/10 bg-white" data-testid="chat-thread-header">
            <button onClick={onBack} className="lg:hidden p-1.5 -ml-1.5 text-ink/60" data-testid="chat-back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <Avatar name={conv.title} group={conv.type !== "direct"} announce={conv.type === "announcement"} online={other ? isOn : undefined} />
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-ink truncate" data-testid="chat-thread-title">{conv.title}</div>
                <div className={`text-xs truncate ${isOn ? "text-emerald-600" : "text-ink/45"}`} data-testid="chat-thread-status">{status}</div>
            </div>
            {conv.type === "group" && onEditGroup && (
                <button onClick={onEditGroup} className="p-2.5 rounded-full text-ink/60 hover:bg-ink/5" title="Edit group" data-testid="chat-edit-group-btn"><Settings2 className="w-5 h-5" /></button>
            )}
            {other && (
                <div className="flex gap-1">
                    <button onClick={() => call("audio")} disabled={!isOn} title={isOn ? "Voice call" : "They're offline"} className="p-2.5 rounded-full text-klein hover:bg-klein/10 disabled:opacity-30" data-testid="chat-voice-call-btn"><Phone className="w-5 h-5" /></button>
                    <button onClick={() => call("video")} disabled={!isOn} title={isOn ? "Video call" : "They're offline"} className="p-2.5 rounded-full text-klein hover:bg-klein/10 disabled:opacity-30" data-testid="chat-video-call-btn"><Video className="w-5 h-5" /></button>
                </div>
            )}
        </div>
    );
}

export function MessageThread({ conv, onBack, onRead, onEditGroup }) {
    const { me, online, subscribe } = useChat();
    const [msgs, setMsgs] = useState([]);
    const [more, setMore] = useState(false);
    const [reads, setReads] = useState(conv.reads || {});
    const [typing, setTyping] = useState("");
    const [pinned, setPinned] = useState(conv.pinned || null);
    const endRef = useRef(null);
    const typingTimer = useRef(null);

    const markRead = useCallback(() => api.post(`/chat/conversations/${conv.id}/read`).then(onRead).catch(() => {}), [conv.id, onRead]);

    useEffect(() => {
        setMsgs([]); setReads(conv.reads || {}); setTyping("");
        api.get(`/chat/conversations/${conv.id}/messages`).then(({ data }) => { setMsgs(data); setMore(data.length === 50); markRead(); });
    }, [conv.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => subscribe((m) => {
        if (m.type === "resync") {
            api.get(`/chat/conversations/${conv.id}/messages`).then(({ data }) => {
                setMsgs((xs) => { const have = new Set(xs.map((x) => x.id)); return [...xs, ...data.filter((x) => !have.has(x.id))].sort((a, b) => (a.created_at < b.created_at ? -1 : 1)); });
                markRead();
            }).catch(() => {});
            return;
        }
        if (m.conversation_id !== conv.id && m.message?.conversation_id !== conv.id) return;
        if (m.type === "message") {
            setMsgs((xs) => (xs.some((x) => x.id === m.message.id) ? xs : [...xs, m.message]));
            setTyping("");
            if (m.message.sender_id !== me.id && document.visibilityState === "visible") markRead();
        } else if (m.type === "read") setReads((r) => ({ ...r, [m.user_id]: m.at }));
        else if (m.type === "pin") setPinned(m.pinned);
        else if (m.type === "typing") {
            setTyping(`${m.name.split(" ")[0]} is typing…`);
            clearTimeout(typingTimer.current);
            typingTimer.current = setTimeout(() => setTyping(""), 4000);
        }
    }), [subscribe, conv.id, me.id, markRead]);

    useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);

    const loadEarlier = async () => {
        const { data } = await api.get(`/chat/conversations/${conv.id}/messages`, { params: { before: msgs[0]?.created_at } });
        setMsgs((xs) => [...data, ...xs]);
        setMore(data.length === 50);
    };

    const lastMine = [...msgs].reverse().find((m) => m.sender_id === me.id);
    const announce = conv.type === "announcement";
    const manage = announce && hasRole(me, "admin", "hr");
    const togglePin = async (id) => {
        try { await api.post(`/chat/conversations/${conv.id}/pin`, { message_id: pinned?.message_id === id ? null : id }); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not pin"); }
    };
    const jump = () => document.getElementById(`msg-${pinned.message_id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    return (
        <div className="flex flex-col h-full min-h-0" data-testid="chat-thread">
            <ThreadHeader conv={conv} online={online} onBack={onBack} onEditGroup={onEditGroup} />
            <PinnedBanner pinned={pinned} canManage={manage} onJump={jump} onUnpin={() => togglePin(pinned.message_id)} />
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-5 space-y-2 bg-[#F4F6FB]" data-testid="chat-messages">
                {more && <button onClick={loadEarlier} className="mx-auto block text-xs text-klein py-1" data-testid="chat-load-earlier">Load earlier messages</button>}
                {msgs.length === 0 && <div className="text-center text-sm text-ink/40 py-10" data-testid="chat-thread-empty">No messages yet — say hello.</div>}
                {msgs.map((m, i) => (
                    <MessageBubble key={m.id} m={m} mine={m.sender_id === me.id} showName={conv.type !== "direct" && msgs[i - 1]?.sender_id !== m.sender_id}
                        seen={!announce && lastMine?.id === m.id ? seenLabel(conv, reads, m, me.id) : ""}
                        pinned={pinned?.message_id === m.id} onPin={manage ? () => togglePin(m.id) : undefined}
                        extra={manage ? <SeenBy msg={m} members={conv.members} reads={reads} /> : null} />
                ))}
                <div ref={endRef} />
            </div>
            <div className="h-5 px-5 text-xs text-ink/50 bg-[#F4F6FB]" data-testid="chat-typing-indicator">{typing}</div>
            <Composer convId={conv.id} canPost={conv.type !== "announcement" || hasRole(me, "admin", "hr")} />
        </div>
    );
}

export default MessageThread;
